import PropTypes from "prop-types";
import MetaTags from "react-meta-tags";
import React, { useState, useEffect } from "react";
import { Alert } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { withRouter, Link } from "react-router-dom";
import { registerUser, apiError } from "../../store/actions";

// Icons from react-icons
import {
  RiToothLine,
  RiHospitalLine,
  RiShieldCheckLine,
  RiMailLine,
  RiPhoneLine,
  RiMapPinLine,
  RiUserLine,
  RiArrowRightLine,
  RiFlashlightLine,
  RiMessage3Line,
} from "react-icons/ri";

import oralstopLogo from "../../assets/images/logos/oralstop-logo.png";

import "./auth-custom.scss";

const Register = (props) => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    location: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState("");

  const { user, registrationError } = useSelector((state) => ({
    user: state.Account?.user,
    registrationError: state.Account?.registrationError,
  }));

  useEffect(() => {
    dispatch(apiError(""));
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (validationError) setValidationError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setValidationError("Please enter doctor or clinic name.");
      return;
    }

    if (!formData.email.trim()) {
      setValidationError("Please enter a valid email address.");
      return;
    }

    if (!formData.mobile.trim()) {
      setValidationError("Please enter your mobile number.");
      return;
    }

    if (!formData.location.trim()) {
      setValidationError("Please enter your clinic location / city.");
      return;
    }

    setIsSubmitting(true);
    dispatch(registerUser(formData));

    setTimeout(() => {
      setIsSubmitting(false);
    }, 1500);
  };

  const features = [
    {
      icon: <RiHospitalLine />,
      title: "Multi-Clinic & Branch Support",
      desc: "Manage multiple dental practice locations & doctor rosters",
    },
    {
      icon: <RiFlashlightLine />,
      title: "Instant Digital EHR Setup",
      desc: "Ready-to-use tooth charting, prescription templates & catalog",
    },
    {
      icon: <RiMessage3Line />,
      title: "Automated Patient Recalls",
      desc: "Smart WhatsApp and SMS appointment reminders",
    },
    {
      icon: <RiShieldCheckLine />,
      title: "Cloud Backup & Compliance",
      desc: "Secure automated records with 256-bit encryption",
    },
  ];

  return (
    <React.Fragment>
      <MetaTags>
        <title>Register Clinic | Oralstop OPD - Clinical Management System</title>
      </MetaTags>

      <div className="dental-auth-wrapper">
        <div className="dental-auth-card">
          {/* Left Hero Panel */}
          <div className="dental-hero-panel">
            <div>
              {/* Brand Logo & Header */}
              <div className="dental-brand-header">
                <img src={oralstopLogo} alt="oralstop.com" height="38" className="dental-brand-logo-img" />
                <div>
                  <span className="dental-brand-sub">Clinical EHR Platform</span>
                </div>
              </div>

              {/* Main Heading */}
              <h2 className="dental-hero-heading">
                Transform Your <span>Dental Clinic</span> Operations Today.
              </h2>
              <p className="dental-hero-desc">
                Set up your clinic profile, register chairs, manage staff, 
                and deliver modern dental patient care in minutes.
              </p>

              {/* Clinical Feature List */}
              <div className="dental-features-list">
                {features.map((feat, idx) => (
                  <div key={idx} className="dental-feature-item">
                    <div className="dental-feature-icon">{feat.icon}</div>
                    <div className="dental-feature-text">
                      <h6>{feat.title}</h6>
                      <p>{feat.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Security Badge */}
            <div className="dental-security-badge">
              <span className="pulse-dot" />
              <RiShieldCheckLine className="me-1" />
              <span>256-bit AES Encrypted & HIPAA Compliant</span>
            </div>
          </div>

          {/* Right Form Panel */}
          <div className="dental-form-panel">
            <div className="dental-form-container">
              <div className="dental-form-header">
                <h3 className="dental-form-title">Create Clinic Workspace</h3>
                <p className="dental-form-subtitle">
                  Sign up for your clinical OPD management account.
                </p>
              </div>

              {/* Alerts */}
              {user && (
                <Alert color="success" className="mb-3 d-flex align-items-center py-2 px-3">
                  <i className="mdi mdi-check-circle-outline me-2 font-size-16" />
                  <div className="font-size-13">Account created successfully! Redirecting to login...</div>
                </Alert>
              )}

              {(registrationError || validationError) && (
                <Alert color="danger" className="mb-3 d-flex align-items-center py-2 px-3">
                  <i className="mdi mdi-alert-circle-outline me-2 font-size-16" />
                  <div className="font-size-13">{registrationError || validationError}</div>
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                {/* Doctor / Clinic Name Field */}
                <div className="custom-auth-group">
                  <label className="custom-auth-label" htmlFor="name-input">
                    Doctor or Clinic Name
                  </label>
                  <div className="custom-input-box">
                    <RiUserLine className="input-icon-prefix" />
                    <input
                      id="name-input"
                      name="name"
                      type="text"
                      className="form-control dental-input-field"
                      placeholder="Dr. John Doe / Smile Dental Clinic"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Email Field */}
                <div className="custom-auth-group">
                  <label className="custom-auth-label" htmlFor="email-input">
                    Email Address
                  </label>
                  <div className="custom-input-box">
                    <RiMailLine className="input-icon-prefix" />
                    <input
                      id="email-input"
                      name="email"
                      type="email"
                      className="form-control dental-input-field"
                      placeholder="doctor@oralstop.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Mobile Field */}
                <div className="custom-auth-group">
                  <label className="custom-auth-label" htmlFor="mobile-input">
                    Mobile Number
                  </label>
                  <div className="custom-input-box">
                    <RiPhoneLine className="input-icon-prefix" />
                    <input
                      id="mobile-input"
                      name="mobile"
                      type="tel"
                      className="form-control dental-input-field"
                      placeholder="9876543210"
                      value={formData.mobile}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Location Field */}
                <div className="custom-auth-group">
                  <label className="custom-auth-label" htmlFor="location-input">
                    Location / City
                  </label>
                  <div className="custom-input-box">
                    <RiMapPinLine className="input-icon-prefix" />
                    <input
                      id="location-input"
                      name="location"
                      type="text"
                      className="form-control dental-input-field"
                      placeholder="Mumbai, Maharashtra"
                      value={formData.location}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="dental-submit-btn mt-3"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span>Registering clinic...</span>
                  ) : (
                    <>
                      <span>Create Clinic Workspace</span>
                      <RiArrowRightLine className="font-size-16" />
                    </>
                  )}
                </button>
              </form>

              {/* Footer */}
              <div className="auth-footer-text">
                <span>Already have an account?</span>
                <Link to="/login" className="signup-link">
                  Sign In
                </Link>
                <div className="mt-2 text-muted font-size-11">
                  © {new Date().getFullYear()} Oralstop OPD · All Rights Reserved
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

Register.propTypes = {
  history: PropTypes.object,
};

export default withRouter(Register);
